import { useEffect, useState } from "react";
import { Trash2, ChevronLeft, ChevronRight, ArchiveRestore, Archive } from "lucide-react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Tooltip from "@/components/shared/Tooltip";
import { TestLabel } from "@/types/testLabel";
import { cn } from "@/lib/utils";
import { LabelRowGrid } from "../TestLabelOverview.types";

type TestLabelDataGridProps = {
  labels: TestLabel[];
  onEdit: (id: number) => void;
  onDelete: (data: TestLabel) => void;
  onArchive: (data: TestLabel) => void;
};

function TestLabelDataGrid(props: TestLabelDataGridProps) {
  const navigate = useNavigate();
  const [labelRows, setLabelRows] = useState<LabelRowGrid[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const rows: LabelRowGrid[] = [];
    props.labels.forEach((label: TestLabel) => {
      const row: LabelRowGrid = {
        id: label.id,
        created_at: label.createdAt.toLocaleDateString(),
        name: label.name,
        maskName: label.mask ? label.mask.name : "",
        description: label.description,
        maskSpecific: label.maskSpecific,
        nOStructures: label.linkedSpecs.length,
        isArchived: label.isArchived,
      };
      rows.push(row);
    });
    setLabelRows(rows);
    // Reset to first page when data changes
    setCurrentPage(1);
  }, [props.labels]);

  // Calculate pagination
  const totalItems = labelRows.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRows = labelRows.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page
  };

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Label Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Mask</TableHead>
            <TableHead className="text-center">N° Structures</TableHead>
            <TableHead className="text-center">Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedRows.map((row) => (
            <TableRow
              key={row.id}
              className={cn(`hover:bg-muted/50 cursor-pointer`, {
                "[&>td]:opacity-60": row.isArchived,
              })}
              onClick={() => navigate(`/test-labels/${row.id}`)}
            >
              <TableCell className="font-medium">{row.name}</TableCell>
              <TableCell>
                <div className="max-w-xs truncate">{row.description || "-"}</div>
              </TableCell>
              <TableCell>{row.maskName}</TableCell>
              <TableCell className="text-center">{row.nOStructures}</TableCell>
              <TableCell className="text-center">{row.created_at}</TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-end gap-2">
                  <Tooltip
                    asChild
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const testLabel = props.labels.find((label) => label.id === row.id);
                          if (testLabel) props.onArchive(testLabel);
                        }}
                      >
                        {row.isArchived ? (
                          <ArchiveRestore className="h-4 w-4" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </Button>
                    }
                    content={row.isArchived ? "Unarchive" : "Archive"}
                  />
                  <Tooltip
                    asChild
                    trigger={
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const testLabel = props.labels.find((label) => label.id === row.id);
                          if (testLabel) props.onDelete(testLabel);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    }
                    content="Delete"
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </p>
        </div>

        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="h-8 w-17.5">
                <SelectValue placeholder={itemsPerPage} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 30, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={pageSize.toString()}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">
              Page {currentPage} of {totalPages}
            </p>
            <div className="flex items-center space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">Previous page</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">Next page</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestLabelDataGrid;
